import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sparkles, BookOpen, ChevronRight } from "lucide-react";

/**
 * Dropdown under the top-bar Help (Info) button. Two entries: reopen the
 * getting-started checklist (a modal owned by AppLayout), and jump to the
 * in-app documentation. Mirrors NotificationPanel's popover behaviour
 * (absolute below the button, click-outside + Escape to close).
 */
export function HelpMenu({
  onClose,
  onOpenChecklist,
}: {
  onClose: () => void;
  onOpenChecklist: () => void;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      role="menu"
      className="absolute right-0 top-full mt-2 w-72 overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200/80 dark:bg-slate-900 dark:ring-slate-700/80 z-50"
    >
      <div className="border-b border-slate-100 px-4 py-3 dark:border-slate-800">
        <p className="text-sm font-bold text-fg-primary">{t("help.title")}</p>
      </div>
      <div className="p-1.5">
        <button
          type="button"
          role="menuitem"
          onClick={onOpenChecklist}
          className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-800"
        >
          <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
            <Sparkles size={16} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium text-fg-primary">
              {t("help.gettingStarted")}
            </span>
            <span className="block text-xs text-fg-secondary">
              {t("help.gettingStartedHint")}
            </span>
          </span>
          <ChevronRight size={15} className="flex-none text-fg-muted" />
        </button>

        <Link
          to="/help"
          role="menuitem"
          onClick={onClose}
          className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-800"
        >
          <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-400">
            <BookOpen size={16} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium text-fg-primary">
              {t("help.documentation")}
            </span>
            <span className="block text-xs text-fg-secondary">
              {t("help.documentationHint")}
            </span>
          </span>
          <ChevronRight size={15} className="flex-none text-fg-muted" />
        </Link>
      </div>
    </div>
  );
}
